import { ArrowRight, Check, CheckCircle2, Minus, Wallet, type LucideIcon } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { useNavigate } from "react-router-dom";

import { canUseCardWallet } from "@/app/auth/canUseCardWallet";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import type { Translations } from "@/shared/lang/interfaces/translationTypes";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { CARD_WALLET_SECTION_ID } from "../constants/cardWalletSection";
import { SettingsSection } from "./SettingsSection";

// The same explainer the cards page opens, loaded only when someone asks for it.
const EnableCardWalletDialog = lazy(() =>
  import("@/modules/dashboard/cards/components/card-wallet/EnableCardWalletDialog").then((module) => ({
    default: module.EnableCardWalletDialog,
  })),
);

type SettingsKey = keyof Translations["settings"];

interface CompareRow {
  label: SettingsKey;
  value: SettingsKey;
  /** Whether this way works: a check, or a dash for what the organization cannot do. */
  available: boolean;
}

interface CompareColumn {
  title: SettingsKey;
  rows: CompareRow[];
  highlighted: boolean;
}

const COLUMNS: CompareColumn[] = [
  {
    title: "card_wallet_compare_without",
    highlighted: false,
    rows: [
      { label: "card_wallet_row_load", value: "card_wallet_row_load_without", available: true },
      { label: "card_wallet_row_withdraw", value: "card_wallet_row_withdraw_without", available: false },
    ],
  },
  {
    title: "card_wallet_compare_with",
    highlighted: true,
    rows: [
      { label: "card_wallet_row_load", value: "card_wallet_row_load_with", available: true },
      { label: "card_wallet_row_withdraw", value: "card_wallet_row_withdraw_with", available: true },
    ],
  },
];

/**
 * The organization's card wallet, as an account decision taken once: what changes with it, and the way to
 * turn it on through the same explainer the cards page opens. Organizations that dismissed the invite on the
 * cards page find it here. Turning it off is support's call, so an active wallet only says so.
 */
export function CardWalletSection() {
  const { t } = useTranslations("settings");
  const { user } = useAuth();
  const navigate = useNavigate();
  const [explainerOpen, setExplainerOpen] = useState(false);
  const enabled = canUseCardWallet(user);

  return (
    <SettingsSection
      id={CARD_WALLET_SECTION_ID}
      eyebrow={t("card_wallet_section_eyebrow")}
      title={t("card_wallet_section_title")}
      sub={t("card_wallet_section_sub")}
      headerRight={
        enabled ? (
          <ColocaBadge variant="success" icon={CheckCircle2}>
            {t("card_wallet_status_on")}
          </ColocaBadge>
        ) : (
          <ColocaBadge variant="secondary">{t("card_wallet_status_off")}</ColocaBadge>
        )
      }
    >
      {enabled ? (
        <>
          <div className="flex items-start gap-3 rounded-md border border-primary/30 bg-primary/5 p-3.5">
            <CheckCircle2
              aria-hidden="true"
              className="mt-0.5 size-4 shrink-0 text-primary dark:text-success"
            />
            <p className="text-[13px] text-foreground">{t("card_wallet_on_body")}</p>
          </div>
          <div className="mt-[18px] flex flex-wrap items-center gap-x-4 gap-y-2">
            <ColocaButton variant="default" iconTrailing={ArrowRight} onClick={() => navigate("/app/cards")}>
              {t("card_wallet_go_to_cards")}
            </ColocaButton>
            <p className="text-xs text-muted-foreground">{t("card_wallet_on_note")}</p>
          </div>
        </>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            {COLUMNS.map((column) => (
              <CompareCard key={column.title} column={column} />
            ))}
          </div>
          <div className="mt-[18px] flex flex-wrap items-center gap-x-4 gap-y-2">
            <ColocaButton variant="primary" icon={Wallet} onClick={() => setExplainerOpen(true)}>
              {t("card_wallet_activate")}
            </ColocaButton>
            <p className="text-xs text-muted-foreground">{t("card_wallet_permanent_note")}</p>
          </div>
        </>
      )}
      {/* Outside the on/off branch: enabling flips the section to "on" while the explainer still shows its
          success, and it has to stay mounted until it is closed. */}
      {explainerOpen && (
        <Suspense fallback={null}>
          <EnableCardWalletDialog open onClose={() => setExplainerOpen(false)} />
        </Suspense>
      )}
    </SettingsSection>
  );
}

function CompareCard({ column }: { column: CompareColumn }) {
  const { t } = useTranslations("settings");
  return (
    <div
      className={cn(
        "rounded-lg border p-4",
        // Card, not muted: the plain column stays quiet without lowering the labels' contrast.
        column.highlighted ? "border-primary/40 bg-primary/5" : "border-border bg-card",
      )}
    >
      <h3
        className={cn(
          "mb-3 font-mono text-[10.5px] uppercase tracking-[0.08em]",
          // Dark `--primary` is a fill, not a text colour: on a dark card it reads at ~2:1.
          column.highlighted ? "text-primary dark:text-foreground" : "text-muted-foreground",
        )}
      >
        {t(column.title)}
      </h3>
      <ul className="flex flex-col gap-3">
        {column.rows.map((row) => (
          <CompareLine key={row.label} row={row} icon={row.available ? Check : Minus} />
        ))}
      </ul>
    </div>
  );
}

function CompareLine({ row, icon: Icon }: { row: CompareRow; icon: LucideIcon }) {
  const { t } = useTranslations("settings");
  return (
    <li className="flex items-start gap-2.5">
      <span
        className={cn(
          "mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full",
          row.available
            ? "bg-primary/10 text-primary dark:bg-primary dark:text-primary-foreground"
            : "bg-muted text-muted-foreground",
        )}
      >
        <Icon aria-hidden="true" className="size-3" />
      </span>
      <div className="min-w-0">
        {/* Not `text-muted-foreground`: on the primary tint of the "with" card it falls under 4.5:1. */}
        <p className="text-[11.5px] text-foreground/70">{t(row.label)}</p>
        <p className="text-[13px] font-medium text-foreground">{t(row.value)}</p>
      </div>
    </li>
  );
}
